// ============================================================
// PlateCrowd - a horde on the plate (plan body-map P5, P8): twenty members of what the horde is made of - a person's
// figure, a vehicle, the goo - the share standing being the share of magnitude left (engine/plate/crowd), the back
// rows smaller and darker. Each member first lays down the ground where it stands, so the nearer hide those behind:
// a person fills its silhouette, a vehicle (traced without one) a broad halo round its lines, the goo its drops. A
// horde has no hit locations: magnitude stands in for wounds, and the readout under the crowd says it.
//
// It keeps the old map's name, `Horde body map`; its words are the MAG stencil and `MAGNITUDE n/max · ATTACKS n` (P7).
// ============================================================

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import clsx from '../../utils/clsx';
import { plateBand } from '../../engine/plate/plateBand';
import {
  byDepth,
  CROWD_SLOTS,
  crowdBox,
  felledSlots,
  membersStanding,
  standingSlots,
  CROWD_LAYOUT,
} from '../../engine/plate/crowd';
import { landed } from '../../engine/plate/burst';
import { IMPACT_TTL_MS, type Impact } from '../../engine/impacts';
import usePlateHits, { type HitPlay } from './usePlateHits';
import { flareClass } from './fx/hits';
import fx from '../../styles/PlateFx.module.css';
import { needsClock, type PlateState } from '../../engine/plate/plateState';
import { crowdExtraHits } from '../../engine/bodyShape';
import type { PlateFigureId } from '../../engine/plate/figureArt';
import type { PlateFigure } from '../../engine/plate/figureFor';
import type { VehicleKind } from '../../engine/vehicleKind';
import type { Target } from '../../types';
import usePlateState from './usePlateState';
import styles from '../../styles/BodyPlate.module.css';
import { GOO_BOX, GOO_DROPS, GooMass } from './PlateGoo';
import { ArtDefs, OpenSight, plateSize, ScanUnavailable, usePlateUid, viewBoxOf } from './plateParts';
import usePlateClock from './usePlateClock';
import { ConditionLayers, WholeBodyDefs } from './fx/conditions';
import { VehicleLayers } from './fx/vehicleConditions';
import { gooColourClass, GooLayers } from './fx/gooConditions';
import { usePlateArt, useVehicleArt } from './usePlateArt';
import PlateObject from './PlateObject';

const ARIA = 'Horde body map';

interface Props {
  /** What each member is: the horde's own figure (figureFor). */
  body: PlateFigure;
  target: Target;
  side?: 'target' | 'actor';
  fill?: boolean;
  /** The svg's name; `Horde body map` by default. */
  ariaLabel?: string;
  /** What just happened to the horde (engine/impacts): the members it felled play the hit and fall (plan H2). */
  impact?: Impact | null;
}

export default function PlateCrowd({ body, ...rest }: Props) {
  if (body.kind === 'vehicle') return <VehicleCrowd kind={body.vehicle} {...rest} />;
  if (body.kind === 'goo') return <GooCrowd {...rest} />;
  // An Object is never a crowd (figureFor says so); should one arrive here it is drawn as the one thing it is.
  if (body.kind === 'object') return <PlateObject target={rest.target} side={rest.side} fill={rest.fill} />;
  return <PersonCrowd figure={body.figure} {...rest} />;
}

type CrowdProps = Omit<Props, 'body'>;

function PersonCrowd({ figure, ...rest }: CrowdProps & { figure: PlateFigureId }) {
  const { art, failed } = usePlateArt(figure);
  if (failed) return <ScanUnavailable ariaLabel={rest.ariaLabel ?? ARIA} className={plateSize(rest.fill)} />;
  const member = art && ((u: string) => <ArtDefs u={u} art={art} silhouette />);
  return (
    <CrowdSvg name={figure} kind="person" member={member} hider="silhouette" lineWeight={art?.lineWeight} {...rest} />
  );
}

function VehicleCrowd({ kind, ...rest }: CrowdProps & { kind: VehicleKind }) {
  const { art, failed } = useVehicleArt(kind);
  if (failed) return <ScanUnavailable ariaLabel={rest.ariaLabel ?? ARIA} className={plateSize(rest.fill)} />;
  const member = art && ((u: string) => <ArtDefs u={u} art={art} />);
  return (
    <CrowdSvg
      name={`vehicle-${kind}`}
      kind="vehicle"
      member={member}
      hider="halo"
      lineWeight={art?.lineWeight}
      {...rest}
    />
  );
}

interface SvgProps extends CrowdProps {
  name: string;
  /** What the members are: which body's layers wrap the crowd. */
  kind: PlateFigure['kind'];
  /** The defs a member draws from (`<u>-art`, and for a silhouette `<u>-sil`), or null while its art loads. */
  member: ((u: string) => ReactNode) | null;
  hider: Hider;
  lineWeight?: number;
}

function CrowdSvg({
  name,
  kind,
  member,
  hider,
  lineWeight = 1,
  target,
  side = 'target',
  fill = false,
  ariaLabel = ARIA,
  impact,
}: SvgProps) {
  const u = usePlateUid('crowd');
  const magnitude = target.horde?.magnitude ?? 0;
  const max = target.horde?.maxMagnitude ?? 0;
  // Broken by the GM's Break and fleeing (P7): the crowd flickers out, back rows first, before it is closed.
  const broken = target.horde?.broken === true;
  const standing = standingSlots(magnitude, max);
  const hits = usePlateHits(impact, null);
  const falling = useFelled(membersStanding(magnitude, max), hits.play);
  const { state, dataStates, glow } = usePlateState(target.conditions);
  const ref = useRef<SVGSVGElement>(null);
  usePlateClock(ref, member !== null && needsClock(state));
  const style = { ...glow, '--lw': lineWeight } as CSSProperties;
  return (
    <svg
      ref={ref}
      viewBox={viewBoxOf(CROWD_LAYOUT.frame)}
      className={crowdClass({ fill, side, kind, broken, state })}
      data-band={plateBand(target, state.dead || broken)}
      style={style}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel}
      data-figure={name}
      data-standing={standing.length}
      data-felled={falling.length || undefined}
      data-broken={broken || undefined}
      data-states={dataStates}
    >
      {member && (
        <>
          {member(u)}
          <defs>
            <WholeBodyDefs u={u} s={state} />
          </defs>
          <CrowdLayers u={u} s={state} kind={kind}>
            {byDepth([...standing, ...falling.map((f) => f.slot)]).map((i, order) => (
              <Member key={i} u={u} slot={i} order={order} hider={hider} fall={falling.find((f) => f.slot === i)} />
            ))}
          </CrowdLayers>
          {state.helpless && <OpenSight box={crowdBox(standing)} margin={60} arm={160} />}
        </>
      )}
      <HordeWords magnitude={magnitude} max={max} broken={broken} prone={state.prone} />
    </svg>
  );
}

/**
 * The horde's conditions wrap the whole crowd (ruling 2026-10-08), as the body's own layers wrap one figure: a crowd of
 * people in a person's, of vehicles in a vehicle's, of the mass in the goo's. The per-location ones (the senses, the
 * cords, the chest's breath) are left to the hero plate: at a member's size they would not read.
 */
const LAYERS_OF = { person: ConditionLayers, vehicle: VehicleLayers, goo: GooLayers, object: ConditionLayers };

function CrowdLayers({
  u,
  s,
  kind,
  children,
}: {
  u: string;
  s: PlateState;
  kind: SvgProps['kind'];
  children: ReactNode;
}) {
  const Layers = LAYERS_OF[kind];
  return (
    <Layers u={u} s={s}>
      {children}
    </Layers>
  );
}

/**
 * How a member lays down its ground to hide those behind: a person (and the goo) fills its solid silhouette
 * (`<u>-sil`); a vehicle, traced without one, strokes a broad halo round its own lines.
 */
type Hider = 'silhouette' | 'halo';
const HIDERS: Record<Hider, { className: string; of: string }> = {
  silhouette: { className: styles.memberGround, of: 'sil' },
  halo: { className: styles.memberHalo, of: 'art' },
};

/** The crowd plate's classes: its size, side, death, the goo's colour, and a broken horde's flight. */
function crowdClass(c: { fill: boolean; side: string; kind: string; broken: boolean; state: PlateState }) {
  const { fill, side, kind, broken, state } = c;
  return clsx(
    plateSize(fill),
    styles.plate,
    side === 'actor' && styles.actor,
    state.dead && styles.dead,
    kind === 'goo' && gooColourClass(state),
    broken && styles.crowdBroken,
  );
}

/**
 * The horde in words (a round-4 ruling, the bench's; plan design-gaps P7): its magnitude as a big stencil over the
 * crowd - BROKEN, in red, once it flees - and under it the strip, the magnitude of its whole and its ATTACKS: the
 * ruling's word for the extra hits a horde's attack carries, one per full ten (`crowdExtraHits`, the rule's one place).
 */
function HordeWords({
  magnitude,
  max,
  broken,
  prone,
}: {
  magnitude: number;
  max: number;
  broken: boolean;
  prone: boolean;
}) {
  return (
    <>
      <text
        className={clsx(styles.hordeMag, broken && styles.hordeBroken)}
        x={CROWD_LAYOUT.mid}
        y={CROWD_LAYOUT.magY}
        textAnchor="middle"
        data-testid="horde-mag"
      >
        {broken ? 'BROKEN' : `MAG ${magnitude}`}
      </text>
      <text className={styles.woundsLabel} x={CROWD_LAYOUT.mid} y={CROWD_LAYOUT.stripY} textAnchor="middle">
        {`MAGNITUDE ${magnitude}/${max} · ATTACKS ${crowdExtraHits(magnitude)}${prone ? ' · PRONE' : ''}`}
      </text>
    </>
  );
}

/**
 * One member: its ground laid down where it stands, then its lines; a back row recedes by fading - over its own
 * opaque ground, that reads as its lines mixed into the ground, and it fades the goo, whose filters take one colour.
 */
function Member({
  u,
  slot,
  order,
  hider,
  fall,
}: {
  u: string;
  slot: number;
  order: number;
  hider: Hider;
  fall?: Fall;
}) {
  const { x, y, scale, tint } = CROWD_SLOTS[slot];
  const { className, of } = HIDERS[hider];
  return (
    <g
      transform={`translate(${(x - 1061 * scale).toFixed(0)} ${(y - 3000 * scale).toFixed(0)}) scale(${scale})`}
      data-slot={slot}
      // A broken horde flickers out back rows first (`crowdBroken`): its place in the drawing order is its turn.
      style={{ '--flee-at': `${(order * FLEE_STEP).toFixed(2)}s` } as CSSProperties}
    >
      <g
        className={clsx(fall && fx.felled, fall && flareClass(fall.type))}
        style={fall && { animationDelay: `${fall.at}s` }}
        data-felled={fall ? true : undefined}
      >
        <use className={className} href={`#${u}-${of}`} fill="currentColor" />
        <use className={styles.loc} href={`#${u}-art`} fill="currentColor" opacity={tint / 100} data-member-lines />
      </g>
    </g>
  );
}

/** How far apart the members of a broken horde go out, back rows first (s). */
const FLEE_STEP = 0.04;

/** A member the hit felled: when it is struck (its share of the burst's hits, in turn) and by what. */
interface Fall {
  slot: number;
  at: number;
  type: HitPlay['burst']['hits'][number]['type'];
}

/** A fall in the standing count, and the burst that claimed it (null until one does). */
interface Drop {
  from: number;
  to: number;
  by: string | null;
}

/**
 * The members a hit fells: when the standing count drops, those who stood and no longer do play the hit and fall -
 * shared out over the burst's hits in turn. The drop and the impact arrive apart (the roster and the log are two
 * syncs): a drop seen first waits for the impact (an impact's life at most); one seen during a burst joins it. A drop
 * belongs to ONE burst - a later attack that fells nobody never replays it. (The plate is one per combatant, so a
 * switch to another horde is not a drop.) (State adjusted during render, as useArrived does.)
 */
function useFelled(count: number, play: HitPlay | null): Fall[] {
  const [seen, setSeen] = useState(count);
  const [drop, setDrop] = useState<Drop | null>(null);
  const by = play?.id ?? null;
  if (count !== seen) {
    setSeen(count);
    setDrop(count < seen ? nextDrop(drop, seen, count, by) : null);
  } else if (by && drop?.by === null) {
    setDrop({ ...drop, by });
  }
  useStaleDrop(drop, setDrop);
  return play && drop?.by === play.id ? fallsOf(drop, play) : [];
}

/** A drop no impact claims within an impact's life was not a hit (a GM's hand edit): it fells no one later. */
function useStaleDrop(drop: Drop | null, setDrop: (next: (d: Drop | null) => Drop | null) => void) {
  useEffect(() => {
    if (!drop || drop.by !== null) return undefined;
    const stale = setTimeout(() => setDrop((d) => (d === drop ? null : d)), IMPACT_TTL_MS);
    return () => clearTimeout(stale);
  }, [drop, setDrop]);
}

/** A new fall: added to one still open (unclaimed, or this burst's own), else from where the crowd stood. */
function nextDrop(drop: Drop | null, from: number, to: number, by: string | null): Drop {
  const open = drop && (drop.by === null || drop.by === by);
  return { from: open ? Math.max(from, drop.from) : from, to, by };
}

/** Each felled member, struck by its share of the burst's landed hits in turn. */
function fallsOf(drop: Drop, play: HitPlay): Fall[] {
  const hits = landed(play.burst.hits);
  return felledSlots(drop.from, drop.to).map((slot, k) => {
    const h = hits[k % Math.max(1, hits.length)];
    return { slot, at: h?.at ?? 0, type: h?.type ?? 'I' };
  });
}

/** The goo study's frame mapped onto a figure's: its foot on the 3000 line, a figure's width wide. */
const GOO_SCALE = 2123 / (GOO_BOX.x1 - GOO_BOX.x0);
const GOO_PLACE = `translate(${1061 - GOO_BOX.mx * GOO_SCALE} ${3000 - GOO_BOX.y1 * GOO_SCALE}) scale(${GOO_SCALE})`;

/**
 * A crowd of the mass: each member's drops filled with the ground (`<u>-sil`), and over them its settled rim alone
 * (`<u>-art`; no inner walls - twenty members, a blur each). No stroke on the lines' use (weight 0): its drops are
 * only the filters' input.
 */
function GooCrowd(rest: CrowdProps) {
  const member = (u: string) => (
    <defs>
      <g id={`${u}-sil`} transform={GOO_PLACE}>
        {GOO_DROPS.map(([x, y, r], i) => (
          <circle key={i} cx={x} cy={y} r={r} />
        ))}
      </g>
      <g id={`${u}-art`} transform={GOO_PLACE}>
        <GooMass u={`${u}m`} volume={false} innerWalls={false} />
      </g>
    </defs>
  );
  return <CrowdSvg name="goo" kind="goo" member={member} hider="silhouette" lineWeight={0} {...rest} />;
}
